import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePipelineStore, Pipeline } from '../store/pipelineStore';
import { api, extractApiError } from '../api/client';
import { Play } from 'lucide-react';

function buildPrefillFromPipeline(pipeline: Pipeline) {
  const ideaTitle = (pipeline.idea?.aiOutput?.ideaSnapshot as string) || pipeline.idea?.rawIdea || '';
  const text = ideaTitle.toLowerCase();
  let targetSegment = 'B2B SMB';
  if (text.includes('marketplace') || text.includes('platform')) targetSegment = 'Marketplace';
  else if (text.includes('enterprise')) targetSegment = 'B2B Enterprise';
  else if (text.includes('consumer') || text.includes('b2c')) targetSegment = 'B2C';
  else if (text.includes('saas') || text.includes('software')) targetSegment = 'SaaS';
  return {
    fromPipelineId: pipeline.id,
    linkedStartupId: pipeline.linkedStartupId,
    name: '',
    description: pipeline.idea?.rawIdea ?? '',
    targetSegment,
    problemStatement: pipeline.idea?.guidedAnswers?.problemSolved ?? '',
    differentiator:
      (pipeline.positioning?.aiOutput?.uniqueMechanism as string) ??
      (pipeline.positioning?.aiOutput?.positioningStatement as string) ??
      '',
  };
}

const C = {
  bg: '#050510',
  surface: '#0d0d1f',
  surface2: '#13132a',
  border: 'rgba(255,255,255,0.06)',
  primary: '#7c6ff7',
  primaryGlow: 'rgba(124,111,247,0.3)',
  accent: '#00d4ff',
  green: '#00e676',
  amber: '#ffb800',
  danger: '#ff4466',
  text: '#f0f0ff',
  text2: '#8888aa',
  text3: '#444466',
};

const STEP_LABELS = ['Profile', 'Idea', 'Reality', 'Position', 'Validate'];

const STATUS_COLORS: Record<Pipeline['status'], string> = {
  active: C.primary,
  incubator: C.amber,
  completed: C.green,
  killed: C.danger,
};

const STATUS_ICONS: Record<Pipeline['status'], string> = {
  active: '▶',
  incubator: '💤',
  completed: '✅',
  killed: '✗',
};

function PipelineCard({ pipeline, onAction, featured = false }: {
  pipeline: Pipeline;
  onAction: () => void;
  featured?: boolean;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const { loadPipelines } = usePipelineStore();

  const handleIncubate = async () => {
    setBusy(true);
    try {
      await api.post(`/pipeline/${pipeline.id}/incubate`);
      await loadPipelines();
      onAction();
    } catch (err) {
      alert(extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleActivate = async () => {
    setBusy(true);
    try {
      await api.post(`/pipeline/${pipeline.id}/activate`);
      await loadPipelines();
      onAction();
    } catch (err) {
      alert(extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const rawIdeaName =
    pipeline.title ||
    (pipeline.idea?.aiOutput?.ideaSnapshot as string ?? '').slice(0, 72) ||
    (pipeline.idea?.rawIdea ?? '').slice(0, 72) ||
    'New Idea';
  const isDefaultName = rawIdeaName === 'New Idea';
  // UX 5: Show idea description snippet as subtitle when name is default
  const ideaSubtitle = isDefaultName
    ? ((pipeline.idea?.rawIdea ?? '') || (pipeline.idea?.guidedAnswers?.problemSolved as string ?? '')).slice(0, 60)
    : '';
  const ideaName = rawIdeaName;
  // UX 5: Timestamp
  const createdDate = pipeline.createdAt ? new Date(pipeline.createdAt) : null;
  const daysAgo = createdDate ? Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
  const timeLabel = daysAgo === 0 ? 'Started today' : daysAgo === 1 ? 'Started yesterday' : daysAgo != null ? `Started ${daysAgo} days ago` : '';

  const founderType = pipeline.userProfile?.aiOutput?.founderType as string ?? '';
  const statusColor = STATUS_COLORS[pipeline.status];
  const stepPct = Math.round((pipeline.currentStep / 5) * 100);
  const currentStepLabel = STEP_LABELS[pipeline.currentStep] ?? 'Done';

  return (
    <div style={{
      background: featured
        ? `linear-gradient(135deg, ${C.primary}12, ${C.accent}08)`
        : C.surface,
      border: `1px solid ${featured ? `${C.primary}50` : C.border}`,
      borderRadius: 18,
      padding: featured ? 28 : 22,
      position: 'relative',
      overflow: 'hidden',
      transition: 'border-color 0.2s',
      animation: 'fadeUp 0.4s ease both',
    }}>
      {/* Glow line top */}
      {featured && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, ${C.primary}, ${C.accent})`,
        }} />
      )}

      {/* Status pill */}
      <div style={{
        position: 'absolute', top: 16, right: 16,
        background: `${statusColor}18`,
        color: statusColor,
        borderRadius: 20, padding: '2px 10px',
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <span>{STATUS_ICONS[pipeline.status]}</span>
        <span>{pipeline.status}</span>
      </div>

      {/* Founder type */}
      {founderType && (
        <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, marginBottom: 6, paddingRight: 80 }}>
          {founderType} · {pipeline.status === 'completed' || pipeline.currentStep >= 5 ? 'Complete ✓' : `Step ${Math.min(pipeline.currentStep + 1, 5)}/5`}
        </div>
      )}

      {/* Idea name — UX 5: muted/italic for default "New Idea" */}
      <h3 style={{
        fontSize: featured ? 18 : 15,
        fontWeight: isDefaultName ? 400 : 700,
        fontStyle: isDefaultName ? 'italic' : 'normal',
        color: isDefaultName ? C.text2 : C.text,
        marginBottom: ideaSubtitle || timeLabel ? 4 : 16,
        paddingRight: 80,
        lineHeight: 1.4,
      }}>
        {ideaName}{ideaName.length >= 72 ? '…' : ''}
      </h3>
      {/* UX 5: Subtitle from idea description */}
      {ideaSubtitle && (
        <p style={{ fontSize: 12, color: C.text3, marginBottom: 4, paddingRight: 80, lineHeight: 1.4 }}>
          {ideaSubtitle}{ideaSubtitle.length >= 60 ? '…' : ''}
        </p>
      )}
      {/* UX 5: Timestamp */}
      {timeLabel && (
        <p style={{ fontSize: 10, color: C.text3, marginBottom: 12 }}>{timeLabel}</p>
      )}
      {!ideaSubtitle && !timeLabel && null}

      {/* Step mini-dots */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10, alignItems: 'center' }}>
        {STEP_LABELS.map((label, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
            <div style={{
              width: i === pipeline.currentStep ? 10 : 7,
              height: i === pipeline.currentStep ? 10 : 7,
              borderRadius: '50%',
              background: i < pipeline.currentStep
                ? C.primary
                : i === pipeline.currentStep
                  ? C.accent
                  : C.surface2,
              margin: '0 auto 3px',
              transition: 'all 0.3s',
              boxShadow: i === pipeline.currentStep ? `0 0 8px ${C.accent}80` : 'none',
            }} />
            <div style={{ fontSize: 8, color: i <= pipeline.currentStep ? C.text3 : '#2a2a3e', textAlign: 'center' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div style={{ background: C.surface2, borderRadius: 3, height: 3, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{
          width: `${stepPct}%`, height: '100%', borderRadius: 3,
          background: `linear-gradient(90deg, ${C.primary}, ${C.accent})`,
          transition: 'width 0.6s ease',
        }} />
      </div>

      {/* Next step hint */}
      <div style={{ fontSize: 11, color: C.text3, marginBottom: 16 }}>
        {pipeline.status === 'completed'
          ? '🎉 All steps complete'
          : `Next: ${currentStepLabel}`}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(`/pipeline/${pipeline.id}`)}
          style={{
            flex: 1,
            background: pipeline.status === 'completed'
              ? `${C.green}18`
              : `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
            border: pipeline.status === 'completed' ? `1px solid ${C.green}50` : 'none',
            borderRadius: 10,
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            padding: '9px 0',
            cursor: 'pointer',
          }}
        >
          {pipeline.status === 'completed' ? '📋 View Blueprint' : '▶ Continue'}
        </button>
        {pipeline.status === 'completed' && (
          pipeline.linkedStartupId ? (
            <button
              onClick={() => navigate(`/startups/${pipeline.linkedStartupId}`)}
              style={{
                flex: 1,
                background: `${C.accent}15`,
                border: `1px solid ${C.accent}40`,
                borderRadius: 10,
                color: C.accent,
                fontSize: 12,
                fontWeight: 700,
                padding: '9px 8px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              🚀 View Startup
            </button>
          ) : (
            <button
              onClick={() => navigate('/startups/new', { state: { prefill: buildPrefillFromPipeline(pipeline) } })}
              style={{
                flex: 1,
                background: `${C.primary}15`,
                border: `1px solid ${C.primary}40`,
                borderRadius: 10,
                color: C.primary,
                fontSize: 12,
                fontWeight: 700,
                padding: '9px 8px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              🏗 Turn Into Startup
            </button>
          )
        )}

        {pipeline.status === 'active' && (
          <button
            onClick={handleIncubate}
            disabled={busy}
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              color: C.text2,
              fontSize: 12,
              padding: '9px 12px',
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            💤
          </button>
        )}
        {pipeline.status === 'incubator' && (
          <button
            onClick={handleActivate}
            disabled={busy}
            style={{
              background: `${C.amber}18`,
              border: `1px solid ${C.amber}50`,
              borderRadius: 10,
              color: C.amber,
              fontSize: 12,
              padding: '9px 12px',
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            🔥
          </button>
        )}
      </div>
    </div>
  );
}

export default function PipelineListPage() {
  const navigate = useNavigate();
  const { allPipelines, loading, error, loadPipelines, createPipeline } = usePipelineStore();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => { document.title = 'Idea Pipeline — StartupScore'; }, []);

  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  const handleCreate = async () => {
    setCreateError('');
    setCreating(true);
    try {
      const pipeline = await createPipeline();
      navigate(`/pipeline/${pipeline.id}`);
    } catch (err) {
      const apiErr = extractApiError(err);
      if (apiErr.code === 'ACTIVE_PIPELINE_EXISTS') {
        setCreateError('Move your active pipeline to the incubator first before starting a new one.');
      } else {
        setCreateError(apiErr.message);
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <div style={{
          width: 48, height: 48, margin: '0 auto 16px',
          borderRadius: '50%',
          background: `conic-gradient(from 0deg, ${C.primary}, ${C.accent}, rgba(124,111,247,0.1), ${C.primary})`,
          animation: 'spin 1.2s linear infinite',
          position: 'relative',
        }}>
          <div style={{ width: 34, height: 34, background: '#0f0f1a', borderRadius: '50%', position: 'absolute', top: 7, left: 7 }} />
        </div>
        <p style={{ color: C.text2, fontSize: 14 }}>Loading your pipelines…</p>
      </div>
    );
  }

  const hasActive = !!allPipelines.active;
  const totalIdeas = (hasActive ? 1 : 0) + allPipelines.incubator.length + allPipelines.completed.length;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', animation: 'fadeUp 0.4s ease' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 40 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <h1 style={{
              fontSize: 30, fontWeight: 900, color: C.text, letterSpacing: -0.5,
              background: `linear-gradient(135deg, ${C.text}, ${C.text2})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Idea Pipeline
            </h1>
            {totalIdeas > 0 && (
              <span style={{
                background: `${C.primary}20`, color: C.primary,
                borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700,
              }}>
                {totalIdeas}
              </span>
            )}
          </div>
          <p style={{ color: C.text2, fontSize: 14 }}>
            Validate your startup ideas step-by-step with AI guidance.
          </p>
        </div>

        <div style={{ textAlign: 'right' }}>
          {createError && (
            <p style={{ color: C.danger, fontSize: 12, marginBottom: 8, maxWidth: 260, lineHeight: 1.4 }}>{createError}</p>
          )}
          <button
            onClick={handleCreate}
            disabled={creating}
            style={{
              background: hasActive
                ? 'transparent'
                : `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
              border: hasActive ? `1px solid ${C.primary}` : 'none',
              borderRadius: 12,
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              padding: '10px 22px',
              cursor: creating ? 'not-allowed' : 'pointer',
              opacity: creating ? 0.7 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {creating ? '…' : '+ Start New Idea'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}40`, borderRadius: 12, padding: 14, marginBottom: 24, color: C.danger, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Active pipeline */}
      {hasActive ? (
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.primary, animation: 'glow-pulse 1.5s ease infinite', boxShadow: `0 0 8px ${C.primary}` }} />
            <h2 style={{ fontSize: 11, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 2 }}>
              Active Project
            </h2>
          </div>
          <PipelineCard pipeline={allPipelines.active!} onAction={loadPipelines} featured />
        </div>
      ) : totalIdeas === 0 ? (
        /* B6: First-run welcome screen */
        <div style={{
          marginBottom: 40,
          background: `linear-gradient(135deg, ${C.primary}12, ${C.accent}08)`,
          border: `1px solid ${C.primary}30`,
          borderRadius: 20,
          padding: '56px 48px',
          textAlign: 'center',
          animation: 'fadeUp 0.4s ease',
        }}>
          <div style={{ fontSize: 64, marginBottom: 20, animation: 'float 3s ease-in-out infinite' }}>🚀</div>
          <h2 style={{ fontSize: 26, fontWeight: 900, color: C.text, marginBottom: 12, letterSpacing: -0.5 }}>
            Validate your startup idea with AI
          </h2>
          <p style={{ color: C.text2, fontSize: 15, lineHeight: 1.7, maxWidth: 460, margin: '0 auto 32px' }}>
            StartupScore guides you through a 5-step pipeline — from raw idea to validation plan — so you build things people actually want.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 36 }}>
            {[
              { icon: '🧠', text: 'AI reality check' },
              { icon: '🎯', text: 'Strategic positioning' },
              { icon: '✅', text: 'Validation plan' },
              { icon: '🏗', text: 'MVP blueprint' },
            ].map(({ icon, text }) => (
              <div key={text} style={{
                background: `${C.primary}10`,
                border: `1px solid ${C.primary}25`,
                borderRadius: 10,
                padding: '8px 16px',
                fontSize: 13,
                color: C.text2,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                <span>{icon}</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
          <button
            onClick={handleCreate}
            disabled={creating}
            style={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
              border: 'none', borderRadius: 14, color: '#fff',
              fontSize: 16, fontWeight: 700, padding: '14px 36px',
              cursor: creating ? 'not-allowed' : 'pointer',
              opacity: creating ? 0.7 : 1,
              boxShadow: `0 8px 32px ${C.primaryGlow}`,
            }}
          >
            {creating ? '…' : '🚀 Validate My Idea'}
          </button>
        </div>
      ) : (
        <div style={{
          marginBottom: 40,
          background: `${C.primary}08`,
          border: `2px dashed ${C.primary}30`,
          borderRadius: 18,
          padding: 48,
          textAlign: 'center',
          animation: 'fadeUp 0.4s ease',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16, animation: 'float 3s ease-in-out infinite' }}>🌱</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>No active pipeline</h3>
          <p style={{ color: C.text2, fontSize: 14, marginBottom: 24 }}>Start validating your next idea with AI-powered guidance.</p>
          <button
            onClick={handleCreate}
            disabled={creating}
            style={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
              border: 'none', borderRadius: 12, color: '#fff',
              fontSize: 15, fontWeight: 700, padding: '12px 28px',
              cursor: creating ? 'not-allowed' : 'pointer',
            }}
          >
            <Play size={15} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Start Validation
          </button>
        </div>
      )}

      {/* Incubator */}
      {allPipelines.incubator.length > 0 && (
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 14 }}>💤</span>
            <h2 style={{ fontSize: 11, fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: 2 }}>
              Idea Incubator ({allPipelines.incubator.length})
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
            {allPipelines.incubator.map((p) => (
              <PipelineCard key={p.id} pipeline={p} onAction={loadPipelines} />
            ))}
          </div>
        </div>
      )}

      {/* Completed */}
      {allPipelines.completed.length > 0 && (
        <details style={{ marginBottom: 40 }}>
          <summary style={{
            fontSize: 11, fontWeight: 700, color: C.green,
            textTransform: 'uppercase', letterSpacing: 2,
            cursor: 'pointer', userSelect: 'none',
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0,
          }}>
            <span>✅</span>
            <span>Completed ({allPipelines.completed.length})</span>
          </summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginTop: 16 }}>
            {allPipelines.completed.map((p) => (
              <PipelineCard key={p.id} pipeline={p} onAction={loadPipelines} />
            ))}
          </div>
        </details>
      )}

    </div>
  );
}
